import React, { useState, useEffect, useRef } from "react";
import "./tableStyle.css";
import {
  reportsApi,
  exportPendingFreightPaymentExcel,
} from "../../Services/Services";
import { Col, Input, Button, Radio, Form, Select, message } from "antd";
import "../AddClients/tableStyle.css";
import { useReactToPrint } from "react-to-print";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import { LoadingOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { debounce } from "../util.js";

const { Option } = Select;

export default function PendingFreightPaymet({ onChange }) {
  const [deliveryOrder, setDeliveryOrder] = useState([]);
  const [orderList, setOrderList] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const limit = 50;
  const [form] = Form.useForm();
  const [isExporting, setIsExporting] = useState(false);
  const currentYear = new Date().getFullYear();

  const [filters, setFilters] = useState({
    do_no: "",
    client_name: "",
    vehicle_no: "",
    year: currentYear,
  });

  useEffect(() => {
    // Set initial form values
    form.setFieldsValue({
      year: currentYear,
      do_no: "",
      client_name: "",
      vehicle_no: "",
    });
    fetchData(0);
  }, []);

  const fetchData = async (offset = 0, appliedFilters = filters) => {
    const response = await reportsApi({
      report_name: "freight_payment_pending",
      com_name: localStorage.getItem("com_name"),
      limit,
      offset,
      do_no: appliedFilters.do_no || "",
      client_name: appliedFilters.client_name || "",
      vehicle_no: appliedFilters.vehicle_no || "",
      year: appliedFilters.year || currentYear,
    });

    if (response) {
      const newData = response.results ? response.results : response;

      if (offset === 0) {
        setOrderList(newData);
        setDeliveryOrder(newData);
      } else {
        setOrderList((prev) => [...prev, ...newData]);
        setDeliveryOrder((prev) => [...prev, ...newData]);
      }

      if (response.next === null || newData.length < limit) {
        setHasMore(false);
      }
    }
  };

  const debouncedFilter = useRef(
    debounce((updatedFilters) => {
      setHasMore(true);
      setDeliveryOrder([]);
      setOrderList([]);
      fetchData(0, updatedFilters);
    }, 800)
  ).current;

  const fetchMoreData = () => {
    const offset = orderList.length;
    fetchData(offset);
  };

  const handleValuesChange = (changedValues, allValues) => {
    // Ensure year is always included
    const updatedFilters = {
      ...filters,
      ...allValues,
    };
    setFilters(updatedFilters);
    debouncedFilter(updatedFilters);
  };

  const getYearsArray = (startYear, endYear) => {
    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  };

  const years = getYearsArray(2023, currentYear);

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      message.loading({ content: "Generating Excel file...", key: "export" });

      // Call the API service function
      const result = await exportPendingFreightPaymentExcel({
        com_name: localStorage.getItem("com_name"),
        year: filters.year,
        do_no: filters.do_no,
        client_name: filters.client_name,
        vehicle_no: filters.vehicle_no,
      });

      if (!result.success) {
        throw new Error(result.error || "Export failed");
      }

      // Create blob and download
      const blob = new Blob([result.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Extract filename from headers or use default
      const contentDisposition = result.headers["content-disposition"];
      let filename = `Pending_Freight_Payment_${filters.year}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success({
        content: "Excel file downloaded successfully!",
        key: "export",
        duration: 3,
      });
    } catch (error) {
      console.error("Export error:", error);
      message.error({
        content: `Failed to export: ${error.message}`,
        key: "export",
        duration: 5,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
        initialValues={{
          year: currentYear,
          do_no: "",
          client_name: "",
          vehicle_no: "",
        }}
      >
        <div className="grid grid-cols-1 sm:grid sm:grid-cols-5 sm:gap-2 m-auto px-5 sm:w-[70%] md:w-[100%]">
          <Col>
            <label>
              <b>Select Year</b>
            </label>
            <br />
            <Form.Item name="year" style={{ marginBottom: 20 }}>
              <Select
                showSearch
                style={{ width: "95%", marginLeft: "2%", marginRight: "2%" }}
                placeholder="Select Year"
              >
                {years.map((year) => (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col>
            <label>
              <b>Search By Do No.</b>
            </label>
            <br />
            <Form.Item name="do_no" style={{ marginBottom: 0 }}>
              <Input placeholder="Search by DO No." />
            </Form.Item>
          </Col>
          <Col>
            <label>
              <b>Search By Party Name</b>
            </label>
            <br />
            <Form.Item name="client_name" style={{ marginBottom: 0 }}>
              <Input placeholder="Search by Party Name." />
            </Form.Item>
          </Col>

          <Col>
            <label>
              <b>Search By Vehicle No</b>
            </label>
            <br />
            <Form.Item name="vehicle_no" style={{ marginBottom: 0 }}>
              <Input placeholder="Search by Vehicle No." />
            </Form.Item>
          </Col>
          <Col>
            <Button
              style={{
                bottom: "-10px",
                margin: "10px",
                backgroundColor: "black",
                color: "white",
                fontWeight: "600",
              }}
              onClick={handlePrint}
              disabled={isExporting}
            >
              <FaFilePdf />
            </Button>
            <Button
              style={{
                bottom: "-10px",
                margin: "10px",
                backgroundColor: "green",
                color: "white",
                fontWeight: "600",
              }}
              onClick={handleExportToExcel}
              loading={isExporting}
              disabled={isExporting}
            >
              <FaFileExcel />
            </Button>
          </Col>
        </div>
      </Form>
      <div
        id="scrollableDiv"
        style={{ maxHeight: "500px", overflow: "auto" }}
        ref={componentRef}
      >
        <table id="my-table" className="w-full">
          <thead className="bg-[#151B54] w-full text-white">
            <tr>
              <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-2 px-1 lg:p-1 sm:text-base">
                Sr. No
              </th>
              <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-2 px-1 lg:p-1 sm:text-base">
                Id
              </th>
              <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-2 px-1 lg:p-1 sm:text-base">
                Party Name
              </th>
              <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                Do No.
              </th>
              <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                Truck Owner Name
              </th>
              <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-2 px-1 lg:p-1 sm:text-base">
                Pan No
              </th>
              <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-2 px-1 lg:p-1 sm:text-base">
                Vehicle No
              </th>
              <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-2 px-1 lg:p-1 sm:text-base">
                Pay Amt
              </th>
            </tr>
          </thead>
          <tbody>
            {deliveryOrder.map((e, index) => (
              <tr
                key={e.id}
                style={{ backgroundColor: e.is_applicable ? "#fbd9d3" : "" }}
              >
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  {index + 1}
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-2 px-1 lg:p-1 sm:text-base">
                  {e.id}
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  {e.client_name}
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  <Link to={`/do-view/${e.do_id}`}>
                    <u style={{ fontWeight: "600", color: "blue" }}>
                      {e.do_no}
                    </u>
                  </Link>
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  {e.truck_owner_name}
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  {e.pan_no}
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  {e.vehicle_no}
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  {e.pay_amount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <InfiniteScroll
          dataLength={deliveryOrder.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={
            deliveryOrder.length > 0 && hasMore ? (
              <div style={{ textAlign: "center", padding: "10px" }}>
                Loading...
              </div>
            ) : null
          }
          scrollableTarget="scrollableDiv"
        />
      </div>
    </div>
  );
}
